import { ATMOSPHERE_CONFIG, TPS_SKY_COLOR_GLSL, type AtmosphereFeatureFlags } from "../engine/atmosphere.js";
import type { Mat4 } from "../engine/math.js";
import { LIGHTING_GLSL, createLightingUniforms, type LightingUniforms } from "../engine/lighting.js";
import type { Renderer } from "../engine/renderer.js";
import { MAX_CHARACTER_JOINTS, type CharacterAsset, type CharacterGeometry, type CharacterVisualGroup } from "./character-asset.js";
import { boundedCharacterWetness } from "./character-animation.js";

const vertexSource = `#version 300 es
precision highp float;
precision highp int;
layout(location=0) in vec3 aPosition;
layout(location=1) in vec3 aNormal;
layout(location=2) in vec4 aColor;
layout(location=3) in uvec4 aJoints;
layout(location=4) in vec4 aWeights;
uniform mat4 uViewProjection;
uniform mat4 uModel;
uniform mat4 uJoints[${MAX_CHARACTER_JOINTS}];
out vec3 vNormal;
out vec3 vWorld;
out vec4 vColor;
void main() {
  mat4 skin =
    uJoints[aJoints.x] * aWeights.x +
    uJoints[aJoints.y] * aWeights.y +
    uJoints[aJoints.z] * aWeights.z +
    uJoints[aJoints.w] * aWeights.w;
  vec4 world = uModel * skin * vec4(aPosition, 1.0);
  vWorld = world.xyz;
  vNormal = normalize(mat3(uModel) * mat3(skin) * aNormal);
  vColor = aColor;
  gl_Position = uViewProjection * world;
}`;

const fragmentSource = `#version 300 es
precision highp float;
in vec3 vNormal;
in vec3 vWorld;
in vec4 vColor;
uniform vec4 uBaseColor;
uniform vec3 uCamera;
uniform vec3 uViewForward;
uniform vec3 uSunDirection;
uniform vec3 uAmbientColor;
uniform vec3 uSunColor;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uFogMaximum;
uniform float uTpsAtmosphere;
uniform float uLightingEnabled;
uniform float uFogEnabled;
uniform float uGradingEnabled;
uniform float uWetness;
out vec4 outColor;

${TPS_SKY_COLOR_GLSL}
${LIGHTING_GLSL}

void main() {
  vec3 normal = normalize(vNormal);
  float sunlight = max(0.0, dot(normal, normalize(uSunDirection)));
  float lightBand = floor(sunlight * 5.0 + 0.5) / 5.0;
  vec3 lightColor = worldLighting(vWorld, normal, lightBand);
  if (uLightingEnabled < 0.5) lightColor = vec3(0.82);
  // The vertex palette stays authoritative. Damp cloth is darker with a small
  // broad sheen; skin receives less darkening and there is no mirror material.
  float skin = smoothstep(0.31, 0.39, vColor.r) * (1.0 - smoothstep(0.35, 0.43, vColor.g));
  float wet = clamp(uWetness, 0.0, 1.0);
  vec3 shaded = vColor.rgb * uBaseColor.rgb * lightColor * (1.0 - wet * mix(0.3, 0.12, skin));
  vec3 toEye = normalize(uCamera - vWorld);
  vec3 halfVector = normalize(toEye + normalize(uSunDirection));
  float dampSheen = pow(max(0.0, dot(normal, halfVector)), 18.0) * sunlight;
  shaded += wet * dampSheen * lightColor * 0.12 * uLightingEnabled;

  if (uGradingEnabled > 0.5) {
    float luma = dot(shaded, vec3(0.299, 0.587, 0.114));
    shaded = mix(vec3(luma), shaded, 0.68);
    float coolShadow = (1.0 - lightBand) * 0.38;
    shaded *= mix(vec3(0.98, 0.98, 0.97), vec3(0.90, 0.97, 1.04), coolShadow);
    shaded = clamp((shaded - vec3(0.22)) * 1.07 + vec3(0.22), 0.0, 1.0);
    shaded = floor(shaded * 48.0 + 0.5) / 48.0;
  }

  vec3 cameraRay = vWorld - uCamera;
  float viewDepth = uTpsAtmosphere > 0.5 ? length(cameraRay) : dot(cameraRay, normalize(uViewForward));
  float distanceFog = smoothstep(uFogNear, uFogFar, viewDepth) * uFogMaximum;
  float fog = uFogEnabled > 0.5 ? distanceFog : 0.0;
  if (fog > 0.001) {
    vec3 fogColor = uTpsAtmosphere > 0.5 ? tpsSkyColor(normalize(cameraRay)) : uFogColor;
    shaded = mix(shaded, fogColor, fog);
  }
  outColor = vec4(shaded, vColor.a * uBaseColor.a);
}`;

export class CharacterRenderer {
  readonly triangleCount: number;
  readonly bufferBytes: number;
  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly lightingUniforms: LightingUniforms;
  private readonly shadowProgram: WebGLProgram;
  private readonly shadowUniforms: {
    readonly viewProjection: WebGLUniformLocation | null;
    readonly model: WebGLUniformLocation | null;
    readonly joints: WebGLUniformLocation | null;
  };
  private readonly vertexArray: WebGLVertexArrayObject;
  private readonly indexCount: number;
  private readonly buffers: readonly WebGLBuffer[];
  private disposed = false;
  private readonly uniforms: {
    readonly viewProjection: WebGLUniformLocation;
    readonly model: WebGLUniformLocation;
    readonly joints: WebGLUniformLocation;
    readonly baseColor: WebGLUniformLocation;
    readonly camera: WebGLUniformLocation;
    readonly viewForward: WebGLUniformLocation;
    readonly sunDirection: WebGLUniformLocation;
    readonly ambientColor: WebGLUniformLocation;
    readonly sunColor: WebGLUniformLocation;
    readonly fogColor: WebGLUniformLocation;
    readonly fogNear: WebGLUniformLocation;
    readonly fogFar: WebGLUniformLocation;
    readonly fogMaximum: WebGLUniformLocation;
    readonly tpsAtmosphere: WebGLUniformLocation;
    readonly lightingEnabled: WebGLUniformLocation;
    readonly fogEnabled: WebGLUniformLocation;
    readonly gradingEnabled: WebGLUniformLocation;
    readonly wetness: WebGLUniformLocation;
  };

  constructor(
    private readonly renderer: Renderer,
    private readonly asset: CharacterAsset,
    _atmosphereFlags: AtmosphereFeatureFlags,
  ) {
    this.gl = renderer.gl;
    this.program = createProgram(this.gl, vertexSource, fragmentSource);
    this.lightingUniforms = createLightingUniforms(this.gl, this.program);
    // The same skin matrices and vertex attributes produce the current pose's
    // depth silhouette; this pass never advances the animation clock.
    this.shadowProgram = createProgram(this.gl, vertexSource, "#version 300 es\nprecision highp float;\nvoid main() {}");
    this.shadowUniforms = {
      viewProjection: this.gl.getUniformLocation(this.shadowProgram, "uViewProjection"),
      model: this.gl.getUniformLocation(this.shadowProgram, "uModel"),
      joints: this.gl.getUniformLocation(this.shadowProgram, "uJoints[0]"),
    };
    const uploaded = createVertexArray(this.gl, asset.geometry);
    this.vertexArray = uploaded.vertexArray; this.buffers = uploaded.buffers;
    this.indexCount = asset.geometry.indices.length;
    this.triangleCount = this.indexCount / 3;
    this.bufferBytes = asset.meshBufferBytes;
    const uniform = (name: string): WebGLUniformLocation => {
      const location = this.gl.getUniformLocation(this.program, name);
      if (location === null) throw new Error(`Missing character shader uniform ${name}`);
      return location;
    };
    this.uniforms = {
      viewProjection: uniform("uViewProjection"),
      model: uniform("uModel"),
      joints: uniform("uJoints[0]"),
      baseColor: uniform("uBaseColor"),
      camera: uniform("uCamera"),
      viewForward: uniform("uViewForward"),
      sunDirection: uniform("uSunDirection"),
      ambientColor: uniform("uAmbientColor"),
      sunColor: uniform("uSunColor"),
      fogColor: uniform("uFogColor"),
      fogNear: uniform("uFogNear"),
      fogFar: uniform("uFogFar"),
      fogMaximum: uniform("uFogMaximum"),
      tpsAtmosphere: uniform("uTpsAtmosphere"),
      lightingEnabled: uniform("uLightingEnabled"),
      fogEnabled: uniform("uFogEnabled"),
      gradingEnabled: uniform("uGradingEnabled"),
      wetness: uniform("uWetness"),
    };
    renderer.meshMemoryBytes += this.bufferBytes;
    renderer.metrics.characterBufferBytes += this.bufferBytes;
    renderer.metrics.characterJoints = asset.joints.length;
  }

  begin(viewProjection: Mat4, camera: readonly number[], target: readonly number[]): void {
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vertexArray);
    gl.uniformMatrix4fv(this.uniforms.viewProjection, false, viewProjection);
    gl.uniform4f(this.uniforms.baseColor, ...this.asset.baseColor);
    gl.uniform3f(this.uniforms.camera, camera[0]!, camera[1]!, camera[2]!);
    const forwardX = target[0]! - camera[0]!;
    const forwardY = target[1]! - camera[1]!;
    const forwardZ = target[2]! - camera[2]!;
    const inverseLength = 1 / (Math.hypot(forwardX, forwardY, forwardZ) || 1);
    gl.uniform3f(this.uniforms.viewForward, forwardX * inverseLength, forwardY * inverseLength, forwardZ * inverseLength);
    gl.uniform3f(this.uniforms.sunDirection, ...ATMOSPHERE_CONFIG.sunDirection);
    gl.uniform3f(this.uniforms.ambientColor, ...ATMOSPHERE_CONFIG.ambientColor);
    gl.uniform3f(this.uniforms.sunColor, ...ATMOSPHERE_CONFIG.sunColor);
    this.renderer.bindLightingUniforms(this.lightingUniforms);
    this.renderer.bindEnvironmentUniforms(this.program);
    const tps = this.renderer.tpsAtmosphereEnabled;
    const fog = tps ? this.renderer.fogProfile : ATMOSPHERE_CONFIG;
    gl.uniform3f(this.uniforms.fogColor, ...fog.fogColor);
    gl.uniform1f(this.uniforms.fogNear, fog.fogNear);
    gl.uniform1f(this.uniforms.fogFar, fog.fogFar);
    gl.uniform1f(this.uniforms.fogMaximum, fog.fogMaximum);
    gl.uniform1f(this.uniforms.tpsAtmosphere, tps ? 1 : 0);
    const flags = this.renderer.atmosphereFeatureFlags;
    gl.uniform1f(this.uniforms.lightingEnabled, flags.lighting ? 1 : 0);
    gl.uniform1f(this.uniforms.fogEnabled, flags.fog ? 1 : 0);
    gl.uniform1f(this.uniforms.gradingEnabled, flags.grading ? 1 : 0);
  }

  draw(model: Mat4, jointPalette: Float32Array, wetness = 0, visualGroups?: ReadonlySet<string>): void {
    const gl = this.gl;
    gl.uniformMatrix4fv(this.uniforms.model, false, model);
    gl.uniformMatrix4fv(this.uniforms.joints, false, jointPalette);
    gl.uniform1f(this.uniforms.wetness, boundedCharacterWetness(wetness));
    for (const group of this.selectedGroups(visualGroups)) {
      gl.drawElements(gl.TRIANGLES, group.indexCount, gl.UNSIGNED_SHORT, group.firstIndex * 2);
      this.renderer.metrics.drawCalls += 1;
      this.renderer.metrics.characterDrawCalls += 1;
      this.renderer.metrics.characterTriangles += group.indexCount / 3;
      this.renderer.metrics.triangles += group.indexCount / 3;
    }
  }

  beginShadow(viewProjection: Mat4): void {
    const gl = this.gl;
    gl.useProgram(this.shadowProgram);
    gl.bindVertexArray(this.vertexArray);
    gl.uniformMatrix4fv(this.shadowUniforms.viewProjection, false, viewProjection);
  }

  drawShadow(model: Mat4, jointPalette: Float32Array, visualGroups?: ReadonlySet<string>): void {
    const gl = this.gl;
    gl.uniformMatrix4fv(this.shadowUniforms.model, false, model);
    gl.uniformMatrix4fv(this.shadowUniforms.joints, false, jointPalette);
    for (const group of this.selectedGroups(visualGroups)) {
      if (!this.renderer.canDrawShadow(group.indexCount / 3)) continue;
      gl.drawElements(gl.TRIANGLES, group.indexCount, gl.UNSIGNED_SHORT, group.firstIndex * 2);
      this.renderer.recordShadowDraw(group.indexCount / 3);
    }
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const buffer of this.buffers) this.gl.deleteBuffer(buffer);
    this.gl.deleteVertexArray(this.vertexArray);
    this.gl.deleteProgram(this.program); this.gl.deleteProgram(this.shadowProgram);
    this.renderer.meshMemoryBytes -= this.bufferBytes;
    this.renderer.metrics.characterBufferBytes -= this.bufferBytes;
  }
  private selectedGroups(selected?: ReadonlySet<string>) {
    if (!this.asset.visualGroups) return [{ id: "body", firstIndex: 0, indexCount: this.indexCount }];
    const ranges: CharacterVisualGroup[] = [];
    for (const group of this.asset.visualGroups) {
      if (group.id !== "body" && !(selected ? selected.has(group.id) : group.id === "jeans_basic")) continue;
      const previous = ranges[ranges.length - 1];
      if (previous && previous.firstIndex + previous.indexCount === group.firstIndex) {
        ranges[ranges.length - 1] = { ...previous, indexCount: previous.indexCount + group.indexCount };
      } else ranges.push(group);
    }
    return ranges;
  }
}

function createVertexArray(gl: WebGL2RenderingContext, geometry: CharacterGeometry): { vertexArray: WebGLVertexArrayObject; buffers: readonly WebGLBuffer[] } {
  const vertexArray = gl.createVertexArray();
  const position = gl.createBuffer();
  const normal = gl.createBuffer();
  const color = gl.createBuffer();
  const joints = gl.createBuffer();
  const weights = gl.createBuffer();
  const indices = gl.createBuffer();
  if (!vertexArray || !position || !normal || !color || !joints || !weights || !indices) {
    throw new Error("Character GPU buffer allocation failed");
  }
  gl.bindVertexArray(vertexArray);
  floatAttribute(gl, position, 0, 3, geometry.positions);
  floatAttribute(gl, normal, 1, 3, geometry.normals);
  floatAttribute(gl, color, 2, 4, geometry.colors);
  gl.bindBuffer(gl.ARRAY_BUFFER, joints);
  gl.bufferData(gl.ARRAY_BUFFER, geometry.joints, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(3);
  gl.vertexAttribIPointer(3, 4, gl.UNSIGNED_BYTE, 0, 0);
  floatAttribute(gl, weights, 4, 4, geometry.weights);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, geometry.indices, gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  return { vertexArray, buffers: [position, normal, color, joints, weights, indices] };
}

function floatAttribute(
  gl: WebGL2RenderingContext,
  buffer: WebGLBuffer,
  location: number,
  width: number,
  data: Float32Array,
): void {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, width, gl.FLOAT, false, 0, 0);
}

function createProgram(gl: WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("Unable to create character shader program");
  const compile = (type: number, source: string): WebGLShader => {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Unable to create character shader");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader) ?? "Character shader error";
      gl.deleteShader(shader);
      throw new Error(message);
    }
    gl.attachShader(program, shader);
    return shader;
  };
  const vertexShader = compile(gl.VERTEX_SHADER, vertex);
  const fragmentShader = compile(gl.FRAGMENT_SHADER, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? "Character shader link error");
  }
  return program;
}
